import { useCallback, useEffect, useMemo } from "react";

import { parseCookies, setCookie } from "nookies";

import {
  decodeOrganizationFromCookies,
  encodeOrganizationToSaveInCookies,
} from "pages/[tenant]/browse/utils/cachedBillboardOrganizationContentUtils";

import { ISubscribedOrg } from "shared/providers/context/types/auth-context";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import { usePlanFeature } from "shared/utils/hooks/usePlanFeature";

import { captureException, captureMessage } from "@sentry/nextjs";

export const useOrganizationConfig = () => {
  const { loggedUser } = useAuthContext();
  const { state, setState } = useGlobalContext();
  const isOrganizationsFeatureEnabled = usePlanFeature("f_orgs");

  const getOrganizationFromCookies = useCallback((): ISubscribedOrg => {
    let result = null;

    try {
      const cookies = parseCookies();

      const hasOrganizationInCookie = !!cookies?.organization;
      if (!hasOrganizationInCookie) return null;

      result = decodeOrganizationFromCookies(cookies?.organization);
    } catch (error) {
      console.log("Error (getOrganizationFromCookies): ", error);
      captureException(error);
      captureMessage("Erro (getOrganizationFromCookies)");
    }

    return result;
  }, [decodeOrganizationFromCookies]);

  const saveOrganizationInCookies = useCallback(
    (organization: ISubscribedOrg) => {
      try {
        setCookie(
          null,
          "organization",
          encodeOrganizationToSaveInCookies(organization),
          {
            maxAge: 60 * 60 * 24 * 30, // 30 days
          },
        );
        setState((prev) => ({
          ...prev,
          tenancy: {
            ...prev?.tenancy,
            organization: {
              ...prev?.tenancy?.organization,
              ...organization,
              showSelectModal: false,
            },
          },
        }));
      } catch (error) {
        console.log("Error (saveOrganizationInCookies): ", error);
        captureException(error);
        captureMessage("Erro (saveOrganizationInCookies)");
      }
    },
    [setState, encodeOrganizationToSaveInCookies],
  );

  const setIsChangingOrganization = (showPageLoader: boolean) => {
    setState((prev) => ({
      ...prev,
      tenancy: {
        ...prev?.tenancy,
        organization: {
          ...prev?.tenancy?.organization,
          showPageLoader,
        },
      },
    }));
  };

  const orgsList = loggedUser?.subscribed_orgs;
  const organizationInCookie = getOrganizationFromCookies();

  const hasOrganizationInCookie = !!organizationInCookie;

  const selectedOrganization: ISubscribedOrg =
    orgsList?.find(
      (organization) => organization?.id === +organizationInCookie?.id,
    ) ?? organizationInCookie;
  const organizationIsMain =
    organizationInCookie?.id === 0 || organizationInCookie?.id === null;
  const userIsAdmin =
    !!loggedUser?.admin ||
    !!loggedUser?.superadmin ||
    loggedUser?.console_role?.slug === "superadmin" ||
    loggedUser?.id === 1 ||
    loggedUser?.id === 2;

  const userHasOnlyOneOrganization = orgsList?.length === 1;
  const shouldAllowUserChangeOrganization: boolean = useMemo(
    () =>
      (!!loggedUser &&
        hasOrganizationInCookie &&
        !!orgsList?.length &&
        isOrganizationsFeatureEnabled &&
        !userHasOnlyOneOrganization) ||
      (userIsAdmin && !!orgsList?.length),
    [
      loggedUser,
      hasOrganizationInCookie,
      orgsList,
      userHasOnlyOneOrganization,
      userIsAdmin,
    ],
  );

  const shouldShowSelectOrganizationModal =
    (state?.tenancy?.organization?.showSelectModal ||
      (!!loggedUser && !!orgsList?.length && !hasOrganizationInCookie)) &&
    isOrganizationsFeatureEnabled &&
    (userIsAdmin ? true : !userHasOnlyOneOrganization);

  useEffect(() => {
    if (!userIsAdmin && userHasOnlyOneOrganization && !hasOrganizationInCookie)
      saveOrganizationInCookies(orgsList?.[0]);
  }, [userHasOnlyOneOrganization, hasOrganizationInCookie]);

  return {
    getOrganizationFromCookies,
    saveOrganizationInCookies,
    setIsChangingOrganization,
    selectedOrganization,
    shouldAllowUserChangeOrganization,
    shouldShowSelectOrganizationModal,
    organizationIsMain,
  };
};
